import React, { useState, FormEvent } from 'react';
import {
  Modal,
  ModalTitle,
  ModalContent,
  ModalActions,
  Button,
  TextInput,
  Label,
  Checkbox,
  Chip,
  Alert,
} from '@c2fo/liquidity';
import { PencilIcon } from '@c2fo/liquidity/icons';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { apiClient } from '../../../lib/apiClient';
import { useSourceUserDivisionsQuery } from '../data/useSourceUserDivisionsQuery';
import type { Division } from '../types';

interface User {
  id: string;
  firstName: string | null;
  lastName: string | null;
  emailAddress: string;
  authServiceUuid: string;
}

interface UserSearchResponse {
  data: User[];
  errors: string[];
  jsonapi: {
    version: string;
  };
}

interface CloneDivisionsModalProps {
  open: boolean;
  onClose: () => void;
  targetUserId: string;
  targetUserEmail: string;
  targetUserName: string;
  onDivisionsChange?: () => void;
}

export const CloneDivisionsModal: React.FC<CloneDivisionsModalProps> = ({
  open,
  onClose,
  targetUserId,
  targetUserEmail,
  targetUserName,
  onDivisionsChange,
}) => {
  const queryClient = useQueryClient();
  const [userQuery, setUserQuery] = useState('');
  const [userSearchResults, setUserSearchResults] = useState<User[]>([]);
  const [selectedSourceUser, setSelectedSourceUser] = useState<User | null>(null);
  const [selectedDivisions, setSelectedDivisions] = useState<Division[]>([]);
  const [userSearchLoading, setUserSearchLoading] = useState(false);
  const [searchError, setSearchError] = useState<string | null>(null);
  const [showConfirmModal, setShowConfirmModal] = useState(false);

  // Use the query hook for fetching divisions
  const {
    data: sourceUserDivisions = [],
    isLoading: divisionsLoading,
    error: divisionsError,
  } = useSourceUserDivisionsQuery(selectedSourceUser?.authServiceUuid);

  // Mutation for cloning divisions
  const cloneDivisionsMutation = useMutation({
    mutationFn: async (divisionsToClone: Division[]) => {
      const divisionsUsers = divisionsToClone.map((division) => ({
        divisionUuid: division.uuid,
        user: {
          authServiceUuid: targetUserId,
        },
        sendInvite: false,
      }));

      await apiClient()
        .post(`broadcast/api/admin/user/${targetUserId}/add_divisions`, {
          json: divisionsUsers,
        })
        .json();
    },
    onSuccess: () => {
      // Invalidate the user divisions query to refetch the updated data
      queryClient.invalidateQueries({ queryKey: ['userDivisions', targetUserId] });
      handleClose();
      if (onDivisionsChange) {
        onDivisionsChange();
      }
    },
  });

  const handleSearchUsers = async (e?: FormEvent) => {
    if (e) {
      e.preventDefault();
    }

    if (!userQuery.trim()) {
      return;
    }

    setUserSearchLoading(true);
    setSearchError(null);
    setUserSearchResults([]);
    setSelectedSourceUser(null);
    setSelectedDivisions([]);

    try {
      const data: UserSearchResponse = await apiClient()
        .get(`api/users/search?query=${encodeURIComponent(userQuery.trim())}`)
        .json<UserSearchResponse>();
      setUserSearchResults(data?.data || []);
    } catch (err) {
      setSearchError(err instanceof Error ? err.message : 'Error searching for users');
      setUserSearchResults([]);
    } finally {
      setUserSearchLoading(false);
    }
  };

  const handleSelectSourceUser = (user: User) => {
    // Prevent selecting the target user as the source user
    if (user.id === targetUserId || user.authServiceUuid === targetUserId) {
      setSearchError('Cannot clone divisions from the same user. Please select a different user.');
      return;
    }

    setSelectedSourceUser(user);
    setSelectedDivisions([]);
  };

  const handleToggleDivision = (division: Division) => {
    const isSelected = selectedDivisions.some((d) => d.uuid === division.uuid);
    if (isSelected) {
      setSelectedDivisions(selectedDivisions.filter((d) => d.uuid !== division.uuid));
    } else {
      setSelectedDivisions([...selectedDivisions, division]);
    }
  };

  const handleSelectAll = () => {
    setSelectedDivisions([...sourceUserDivisions]);
  };

  const handleSelectNone = () => {
    setSelectedDivisions([]);
  };

  const handleCloneClick = () => {
    if (selectedDivisions.length === 0 || !selectedSourceUser) {
      return;
    }

    setShowConfirmModal(true);
  };

  const handleConfirmClone = () => {
    if (selectedDivisions.length === 0 || !selectedSourceUser) {
      return;
    }

    setShowConfirmModal(false);
    cloneDivisionsMutation.mutate(selectedDivisions);
  };

  const handleClose = () => {
    setUserQuery('');
    setUserSearchResults([]);
    setSelectedSourceUser(null);
    setSelectedDivisions([]);
    setSearchError(null);
    setShowConfirmModal(false);
    onClose();
  };

  return (
    <Modal open={open} onClose={handleClose} size={showConfirmModal ? 'sm' : 'auto'}>
      <ModalTitle>{showConfirmModal ? 'Confirm Division Assignment' : 'Clone Divisions'}</ModalTitle>
      <ModalContent>
        {showConfirmModal ? (
          <div className="mb-4" data-testid="clone-confirm-step">
            <Alert
              type="info"
              variant="standard"
              full
              description={`You are adding ${targetUserName} to ${selectedDivisions.length} ${
                selectedDivisions.length > 1 ? 'divisions' : 'division'
              }. This user will now be able to login and view invoices from:`}
            />
            <div className="mt-4">
              <div className="text-sm font-medium mb-2">Divisions:</div>
              <ul className="list-disc list-inside space-y-1 text-sm text-gray-700 max-h-60 overflow-y-auto">
                {selectedDivisions.map((division) => (
                  <li key={division.uuid}>{division.name}</li>
                ))}
              </ul>
            </div>
            <div className="mt-4 text-sm text-gray-600">Are you sure you want to continue?</div>
          </div>
        ) : (
          <div className="mb-4">
            <div className="flex items-center justify-between mb-4">
              <div className="flex-1 mr-4">
                <form onSubmit={handleSearchUsers} className="flex gap-2">
                  <div className="flex-1">
                    <Label htmlFor="userQuery" className="sr-only">
                      Find a user to clone from
                    </Label>
                    <TextInput
                      id="userQuery"
                      value={userQuery}
                      onChange={(e) => setUserQuery(e.target.value)}
                      placeholder="Find a user to clone from"
                      className="w-full"
                      disabled={userSearchLoading}
                    />
                  </div>
                  <Button type="submit" variant="secondary" disabled={userSearchLoading || !userQuery.trim()}>
                    {userSearchLoading ? 'Searching...' : 'Search'}
                  </Button>
                </form>
              </div>
              <div className="text-sm">
                <span className="font-medium">Cloning To:</span>{' '}
                <span className="text-red-600 font-medium">{targetUserEmail}</span>
              </div>
            </div>

            {searchError && (
              <div className="mb-4 p-2 bg-red-50 border border-red-200 rounded text-red-600 text-sm" role="alert">
                {searchError}
              </div>
            )}

            {userSearchResults.length > 0 && !selectedSourceUser && (
              <div className="mb-4 border border-gray-300 rounded p-2 bg-white max-h-60 overflow-y-auto">
                {userSearchResults.map((user) => (
                  <div
                    key={user.authServiceUuid}
                    className="flex items-center justify-between p-2 hover:bg-gray-50 border-b border-gray-200 last:border-b-0"
                  >
                    <div>
                      <div className="font-medium">
                        {user.firstName && user.lastName ? `${user.firstName} ${user.lastName}` : '-'}
                      </div>
                      <div className="text-sm text-gray-600">{user.emailAddress}</div>
                    </div>
                    <Button variant="secondary" size="sm" onClick={() => handleSelectSourceUser(user)}>
                      Select
                    </Button>
                  </div>
                ))}
              </div>
            )}

            {selectedSourceUser && (
              <div className="mb-4">
                <div className="flex items-center justify-between mb-2">
                  <div>
                    <span className="font-medium">Cloning From:</span>{' '}
                    <span className="text-pink-600 font-medium">{selectedSourceUser.emailAddress}</span>
                    <button
                      type="button"
                      onClick={() => {
                        setSelectedSourceUser(null);
                        setSelectedDivisions([]);
                      }}
                      className="ml-2 text-gray-600 hover:text-gray-800"
                      aria-label="Change source user"
                    >
                      <PencilIcon className="w-4 h-4" />
                    </button>
                  </div>
                  <div className="flex gap-2">
                    <Button
                      variant="secondary"
                      size="sm"
                      onClick={handleSelectAll}
                      disabled={sourceUserDivisions.length === 0}
                    >
                      All
                    </Button>
                    <Button
                      variant="secondary"
                      size="sm"
                      onClick={handleSelectNone}
                      disabled={sourceUserDivisions.length === 0}
                    >
                      None
                    </Button>
                  </div>
                </div>

                {divisionsLoading && <div className="text-center py-4 text-gray-600">Loading divisions...</div>}

                {divisionsError && (
                  <div className="mb-4 p-2 bg-red-50 border border-red-200 rounded text-red-600 text-sm" role="alert">
                    {divisionsError instanceof Error ? divisionsError.message : 'Error loading divisions'}
                  </div>
                )}

                {!divisionsLoading && sourceUserDivisions.length === 0 && !divisionsError && (
                  <div className="border border-blue-300 rounded p-4 bg-blue-50 text-blue-800 text-center">
                    No divisions found to copy.
                  </div>
                )}

                {!divisionsLoading && sourceUserDivisions.length > 0 && (
                  <div className="border border-gray-300 rounded overflow-hidden">
                    <table className="w-full text-xs">
                      <thead className="bg-gray-50">
                        <tr>
                          <th className="p-2 text-left border-b border-gray-300 w-8"></th>
                          <th className="p-2 text-left border-b border-gray-300">Supplier</th>
                          <th className="p-2 text-left border-b border-gray-300">Market</th>
                          <th className="p-2 text-center border-b border-gray-300">Signed Up</th>
                          <th className="p-2 text-left border-b border-gray-300">Last Play</th>
                          <th className="p-2 text-left border-b border-gray-300">Pay Date</th>
                          <th className="p-2 text-right border-b border-gray-300">Invoices</th>
                          <th className="p-2 text-right border-b border-gray-300">Eligible</th>
                        </tr>
                      </thead>
                      <tbody>
                        {sourceUserDivisions.map((division, index) => {
                          const isSelected = selectedDivisions.some((d) => d.uuid === division.uuid);
                          return (
                            <tr
                              key={`${division.uuid}-${index}`}
                              className={`border-b border-gray-200 ${index % 2 === 0 ? 'bg-white' : 'bg-gray-50'}`}
                            >
                              <td className="p-2">
                                <Checkbox
                                  checked={isSelected}
                                  onChange={() => handleToggleDivision(division)}
                                  aria-label={`Select ${division.name}`}
                                />
                              </td>
                              <td className="p-2">
                                <div>{division.name}</div>
                                {division.makerDivisionName && (
                                  <div className="text-xs text-gray-500">{division.makerDivisionName}</div>
                                )}
                              </td>
                              <td className="p-2">{division.makerDivisionName || division.marketType || '-'}</td>
                              <td className="p-2 text-center">
                                <Chip
                                  type={division.signedUpCount > 0 ? 'primary' : 'default'}
                                  variant="filled"
                                  size="xs"
                                  label={division.signedUpCount > 0 ? 'Yes' : 'No'}
                                />
                              </td>
                              <td className="p-2">
                                {division.lastClearDate
                                  ? new Date(division.lastClearDate).toISOString().split('T')[0]
                                  : '-'}
                              </td>
                              <td className="p-2">
                                {division.payDate ? new Date(division.payDate).toISOString().split('T')[0] : '-'}
                              </td>
                              <td className="p-2 text-right">{division.eligibleInvoiceCount || 0}</td>
                              <td className="p-2 text-right">
                                $
                                {(division.eligibleInvoiceAmount || 0).toLocaleString('en-US', {
                                  minimumFractionDigits: 2,
                                  maximumFractionDigits: 2,
                                })}
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            )}
          </div>
        )}
      </ModalContent>
      <ModalActions>
        {showConfirmModal ? (
          <>
            <Button
              variant="secondary"
              onClick={() => setShowConfirmModal(false)}
              disabled={cloneDivisionsMutation.isPending}
            >
              Cancel
            </Button>
            <Button
              variant="primary"
              onClick={handleConfirmClone}
              loading={cloneDivisionsMutation.isPending}
              disabled={cloneDivisionsMutation.isPending}
            >
              Confirm
            </Button>
          </>
        ) : (
          <>
            <Button variant="secondary" onClick={handleClose} disabled={cloneDivisionsMutation.isPending}>
              Close
            </Button>
            {selectedDivisions.length > 0 && (
              <Button
                variant="primary"
                onClick={handleCloneClick}
                loading={cloneDivisionsMutation.isPending}
                disabled={cloneDivisionsMutation.isPending}
              >
                Add {targetUserName} to {selectedDivisions.length} division{selectedDivisions.length > 1 ? 's' : ''}
              </Button>
            )}
          </>
        )}
      </ModalActions>
    </Modal>
  );
};
